import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { findReferencedExternalFiles, missingExternalFiles } from "../model/inp/externalFiles.ts";
import type { StoredProject } from "../model/storage.ts";
import type { useSimulationWorker } from "./useSimulationWorker.ts";

/**
 * Gates `runSimulation` on referenced-but-not-yet-supplied external climate
 * files (checked at RUN time, not at open time), and the "Warnings"
 * banner's own dismissal — reset only when a NEW run actually completes, so
 * `simulationWorker.stop()`'s null-out (a project switch) can't resurrect an
 * already-dismissed warning list for nothing.
 */
export function useSimulationRun(
  loaded: StoredProject | null,
  setLoaded: Dispatch<SetStateAction<StoredProject | null>>,
  simulationWorker: ReturnType<typeof useSimulationWorker>,
  openExternalFilesDialog: () => void
) {
  // Checked at RUN time (ExternalFilesDialog) rather than at open time.
  const externalFileRefs = useMemo(
    () => (loaded ? findReferencedExternalFiles(loaded.rawSections) : []),
    [loaded]
  );
  const externalFilesMissing = useMemo(
    () => missingExternalFiles(externalFileRefs, loaded?.externalFiles),
    [externalFileRefs, loaded?.externalFiles]
  );

  // Dismissible independently of `simulationWorker.results` itself, so
  // re-showing a previous run's results doesn't resurrect an
  // already-dismissed warning list.
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  useEffect(() => {
    if (simulationWorker.results) setWarningsDismissed(false);
  }, [simulationWorker.results]);

  /**
   * Runs in a Worker (`simulateProject.worker.ts`) rather than on the main
   * thread — a real-scale project used to freeze the entire tab for the
   * whole run with no feedback at all. Switching PROJECTS mid-run calls
   * `simulationWorker.stop()`, terminating the worker so its `onmessage` can
   * never fire again — otherwise a run's results could silently attach to a
   * later, unrelated project's element names. Deliberately doesn't cancel a
   * still-running simulation over an in-place EDIT to the same project
   * (undo/redo/mutateRaw call `simulationWorker.clearResults()` instead).
   *
   * Gates on `missingExternalFiles` (checked at RUN time, not at open time)
   * before touching the worker. `externalFiles` rides along in the SAME
   * `start()` call as `rawSections` even if some referenced files remain
   * missing — `simulateProject.ts`'s own warning path (zero rainfall for
   * that source) covers that gracefully.
   */
  function runSimulation() {
    if (!loaded || simulationWorker.simulating) return;
    if (externalFilesMissing.length > 0) {
      openExternalFilesDialog();
      return;
    }
    simulationWorker.start({
      rawSections: loaded.rawSections,
      externalFiles: loaded.externalFiles,
    });
  }

  /** Merges one user-picked external file's content into `loaded.externalFiles` — the debounced autosave effect persists it, so a file supplied once doesn't need re-supplying after a reload. Keyed by `basename` (`ExternalFilesDialog`'s own per-row slot, not the picked file's local name). */
  function supplyExternalFile(basename: string, _fileName: string, content: string) {
    setLoaded((current) => {
      if (!current) return current;
      const next = new Map(current.externalFiles ?? []);
      next.set(basename, content);
      return { ...current, externalFiles: next };
    });
  }

  return {
    externalFilesMissing,
    warningsDismissed,
    setWarningsDismissed,
    runSimulation,
    supplyExternalFile,
  };
}
